import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "@/contexts/auth/useAuth";

import type { BoostBillingAddress } from "@/api/boosts";

/** Also the legacy un-scoped key, which `pruneLegacyEntry` deletes on sight. */
const STORAGE_KEY_PREFIX = "boost.billing_address";

// Opaque id, never the handle: localStorage keys are enumerable by any script
// on the origin, which would publish who has transacted on this device.
function storageKeyFor(accountId: string | null): string {
  return `${STORAGE_KEY_PREFIX}.${accountId ?? "anon"}`;
}

function safeLocalStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Clears by prefix, not by account: `auth-provider.tsx` calls this outside
 * React, and at logout the departing account may already be gone.
 */
function clearStoredBoostBillingAddress(): void {
  const storage = safeLocalStorage();
  if (!storage) return;
  const doomed: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key && key.startsWith(STORAGE_KEY_PREFIX)) doomed.push(key);
  }
  doomed.forEach((key) => storage.removeItem(key));
}

function parse(storage: Storage, key: string): BoostBillingAddress | null {
  const raw = storage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as BoostBillingAddress;
    // Non-empty, not merely `typeof string`: `{ country: "" }` would suppress
    // the form and send empty fields to the tax quote.
    const isNonEmpty = (v: unknown): v is string =>
      typeof v === "string" && v.trim() !== "";
    if (
      !parsed ||
      !isNonEmpty(parsed.country) ||
      !isNonEmpty(parsed.region) ||
      !isNonEmpty(parsed.postal_code) ||
      !isNonEmpty(parsed.city) ||
      (parsed.street_address !== undefined &&
        typeof parsed.street_address !== "string")
    ) {
      storage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    storage.removeItem(key);
    return null;
  }
}

/**
 * The legacy key records no owner, so adopting it would hand one buyer's
 * address to whoever boosts first on a shared device.
 */
function pruneLegacyEntry(storage: Storage, key: string): null {
  if (key === STORAGE_KEY_PREFIX) return null;
  storage.removeItem(STORAGE_KEY_PREFIX);
  return null;
}

function read(key: string): BoostBillingAddress | null {
  const storage = safeLocalStorage();
  if (!storage) return null;
  return parse(storage, key) ?? pruneLegacyEntry(storage, key);
}

/**
 * Persists the first address a buyer submits so later boosts skip the form.
 * Stands in until the backend adds per-user persistence (TMT-85).
 */
function useBoostBillingAddress(): {
  address: BoostBillingAddress | null;
  save: (next: BoostBillingAddress) => void;
  clear: () => void;
} {
  const { currentAccount } = useAuth();
  const storageKey = useMemo(
    () => storageKeyFor(currentAccount?.id ?? null),
    [currentAccount?.id],
  );

  const [address, setAddress] = useState<BoostBillingAddress | null>(() =>
    read(storageKey),
  );

  useEffect(() => {
    setAddress(read(storageKey));
  }, [storageKey]);

  // Cross-tab sync only: same-tab writes fire no `storage` event, which is why
  // every consumer in one tree must share a single instance of this hook.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === storageKey) setAddress(read(storageKey));
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [storageKey]);

  const save = useCallback(
    (next: BoostBillingAddress) => {
      const storage = safeLocalStorage();
      try {
        storage?.setItem(storageKey, JSON.stringify(next));
      } catch {
        // Quota exceeded — silently fall back to session-only state.
      }
      setAddress(next);
    },
    [storageKey],
  );

  const clear = useCallback(() => {
    safeLocalStorage()?.removeItem(storageKey);
    setAddress(null);
  }, [storageKey]);

  return { address, save, clear };
}

export { useBoostBillingAddress, clearStoredBoostBillingAddress };
